import React, {useState, useEffect, MouseEvent, FC} from 'react';
import moment from 'moment';
import MomentUtils from '@date-io/moment';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import Box from '@material-ui/core/Box';
import Popover from '@material-ui/core/Popover';
import {withStyles} from '@material-ui/core';

import useLocalization from '../../hooks/useLocalization';
import {
    DayRangePicker,
    WeekRangePicker,
    MonthRangePicker,
    YearMonthPicker,
    InfoPanel,
    useRangePickerData
} from '../../components';
import {
    getDateline,
    GlobalDatePickerProps,
    CalendarHeaderButton,
    DatePickerContainer,
    isPreviousMonthMonday,
    isFuture,
    getYearMonthDate,
    isSameDateRange
} from '../global-date-picker';
import PopperInput from '../common/PopperInput';
import useTimeRangeWarning from '../v2/sections/fortnite-games/components/time-range-limit/useTimeRangeWarning';

// @ts-ignore
import {Theme} from '@epic-ui/epic';

import {TimeScale, DateMoment} from '../../types';

export const StyledPopover = withStyles((theme: Theme) => ({
    paper: {
        marginTop: theme.spacing(1),
        backgroundColor: theme.palette.background.overlays,

        width: `calc(100% - ${theme.spacing(8)}px)`,

        [theme.breakpoints.up('fsm')]: {
            width: `calc(100% - ${theme.spacing(24)}px)`
        },
        [theme.breakpoints.up('fmd')]: {
            width: 430
        },

        '& [class*=pickerView]': {
            minWidth: '100%',
            maxWidth: '100%',
            minHeight: 250
        }
    }
}))(Popover);

const CODES = {
    today: 'epic.affiliate.fortnite_games.date_select.today',
    infoMessage: 'epic.affiliate.fortnite_games.date_range_picker.calendar.message'
};

const isTimeScaleLive = (timeScale: TimeScale) => timeScale === TimeScale.LIVE;
const isTimeScaleHourly = (timeScale: TimeScale) => timeScale === TimeScale.HOURLY;

const isDailyCalendar = (timeScale: TimeScale) =>
    isTimeScaleLive(timeScale) || isTimeScaleHourly(timeScale) || timeScale === TimeScale.DAILY;
const isWeeklyCalendar = (timeScale: TimeScale) => timeScale === TimeScale.WEEKLY;
const isMonthlyCalendar = (timeScale: TimeScale) => timeScale === TimeScale.MONTHLY;

export interface WidgetDatePickerProps extends GlobalDatePickerProps {
    timeScale: TimeScale;
}

const WidgetDatePicker: FC<WidgetDatePickerProps> = ({
    timeScale,
    selectedDate: selected,
    startDate: start,
    endDate: end,
    onRangeChange
}: WidgetDatePickerProps) => {
    const getMessage = useLocalization();
    const {
        isPreselection,
        selectedDate,
        startDate,
        endDate,
        setIsPreselection,
        handleDayChange,
        handleWeekChange,
        handleMonthChange,
        handleYearMonthChange,
        setCalendar
    } = useRangePickerData({
        selectedDate: selected,
        startDate: start,
        endDate: end
    });
    const [checkTimeRange] = useTimeRangeWarning();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isYearMonthCalendar, setIsYearMonthCalendar] = useState(false);
    const isNewDateRange = !isSameDateRange({startDate: start, endDate: end}, {startDate, endDate});

    const open = Boolean(anchorEl);
    const id = open ? 'date-picker-popper' : undefined;

    const dateline = getDateline({startDate, endDate, message: getMessage(CODES.today)});

    const handleYearMonthCalendarSwitch = () => {
        setIsYearMonthCalendar(!isYearMonthCalendar);
    };

    const handleYearMonthRangeChange = (date: moment.Moment) => {
        const selectedDate = isFuture(date) ? getYearMonthDate(timeScale) : date;

        handleYearMonthChange(selectedDate);
    };

    const togglePopoverState = (event: MouseEvent<HTMLElement>) => {
        if (isTimeScaleLive(timeScale)) {
            return;
        }
        if (anchorEl) {
            if (isNewDateRange) {
                checkTimeRange(
                    {startDate, endDate, timeScale},
                    {
                        onConfirm: () => setAnchorEl(null)
                    }
                );
            } else {
                setAnchorEl(null);
            }
            return;
        }

        setAnchorEl(event.currentTarget);
    };

    const handleWeekClick = (date: DateMoment) => {
        if (isPreviousMonthMonday(date)) {
            handleWeekChange(date);
        }
    };

    useEffect(() => {
        setCalendar({startDate: start, endDate: end, selectedDate: selected});
    }, [start, end, selected]);

    useEffect(() => {
        if (!open) {
            setIsPreselection(true);
            onRangeChange({startDate, endDate, selectedDate});
            setIsYearMonthCalendar(false);
        }
    }, [open]);

    return (
        <>
            <PopperInput
                disabled={isTimeScaleLive(timeScale)}
                open={open}
                value={dateline}
                onClick={togglePopoverState}
            />
            <StyledPopover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={togglePopoverState}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}>
                <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils}>
                    <>
                        {isDailyCalendar(timeScale) && (
                            <DatePickerContainer>
                                {isYearMonthCalendar ? (
                                    <YearMonthPicker
                                        startDate={startDate}
                                        endDate={endDate}
                                        value={selectedDate}
                                        onChange={handleYearMonthRangeChange}
                                        onMonthChange={handleYearMonthCalendarSwitch}
                                        onCalendarSwitch={handleYearMonthCalendarSwitch}
                                    />
                                ) : (
                                    <CalendarHeaderButton onClick={handleYearMonthCalendarSwitch}>
                                        <DayRangePicker
                                            autoOk
                                            disableToolbar
                                            selectedDate={selectedDate}
                                            startDate={startDate}
                                            endDate={endDate}
                                            variant="static"
                                            openTo="date"
                                            value={selectedDate}
                                            onChange={handleDayChange}
                                            isPreselection={isPreselection}
                                        />
                                    </CalendarHeaderButton>
                                )}
                            </DatePickerContainer>
                        )}

                        {isWeeklyCalendar(timeScale) && (
                            <DatePickerContainer>
                                {isYearMonthCalendar ? (
                                    <YearMonthPicker
                                        startDate={startDate}
                                        endDate={endDate}
                                        value={selectedDate}
                                        onChange={handleYearMonthRangeChange}
                                        onMonthChange={handleYearMonthCalendarSwitch}
                                        onCalendarSwitch={handleYearMonthCalendarSwitch}
                                    />
                                ) : (
                                    <CalendarHeaderButton onClick={handleYearMonthCalendarSwitch}>
                                        <WeekRangePicker
                                            autoOk
                                            disableToolbar
                                            selectedDate={selectedDate}
                                            startDate={startDate}
                                            endDate={endDate}
                                            variant="static"
                                            openTo="date"
                                            value={selectedDate}
                                            onChange={handleWeekChange}
                                            isPreselection={isPreselection}
                                            onCalendarClick={handleWeekClick}
                                        />
                                        <Box mb={2}>
                                            <InfoPanel message={getMessage(CODES.infoMessage)} />
                                        </Box>
                                    </CalendarHeaderButton>
                                )}
                            </DatePickerContainer>
                        )}

                        {isMonthlyCalendar(timeScale) && (
                            <DatePickerContainer>
                                <MonthRangePicker
                                    startDate={startDate}
                                    endDate={endDate}
                                    onChange={handleMonthChange}
                                    isPreselection={isPreselection}
                                />
                            </DatePickerContainer>
                        )}
                    </>
                </MuiPickersUtilsProvider>
            </StyledPopover>
        </>
    );
};

export default WidgetDatePicker;
